import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import {
  getShortenedLinkPreviewUrl,
  SMS_SHORT_LINK_PREVIEW_CODE,
} from "@redotech/redo-marketing-common/shortened-link-display";
import {
  getContactCardAttachment,
  getImageAttachment,
} from "@redotech/redo-marketing-templates-common/sms-template-utils";
import { RedoMerchantSmsTemplatesRpcClientContext } from "@redotech/redo-merchant-app-common/sms-templates-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { sanitizeSmsContent } from "@redotech/redo-merchant-marketing-app/sms-builder/common/sms-content-parser";
import { ContactCard } from "@redotech/redo-web/contact-card";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";

export function SmsPreviewContent({ templateId }: { templateId: string }) {
  const { t } = useTranslation();
  const marketingRpcClient = useRequiredContext(
    RedoMerchantSmsTemplatesRpcClientContext,
  );
  const team = useRequiredContext(TeamContext);
  const brandKit = team.settings.brandKit;

  const getLinkPreviewUrl = () =>
    getShortenedLinkPreviewUrl(
      team.settings?.marketing,
      SMS_SHORT_LINK_PREVIEW_CODE,
    );

  const templateLoad = useLoad(async () => {
    const result = await marketingRpcClient.getSmsTemplateByIdPopulated({
      id: templateId,
    });
    return result.template;
  }, [templateId, marketingRpcClient]);

  const { pending: isLoading, error, value: template } = templateLoad;

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="lg" w="full">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  if (error || !template) {
    return (
      <Flex align="center" justify="center" p="lg" w="full">
        <Text fontSize="xs" textAlign="center" textColor="secondary">
          {t("Template not found")}
        </Text>
      </Flex>
    );
  }

  const imageAttachment = getImageAttachment(template);
  const textContent =
    template.aiConfig?.enabled && template.aiConfig?.contentPreview
      ? template.aiConfig.contentPreview
      : `${template.prefixContent || ""}${template.content || ""}`;

  const finalTextContent = template.autoShortenLinks
    ? sanitizeSmsContent(
        textContent,
        template.autoShortenLinks,
        getLinkPreviewUrl(),
        template.schemaType,
      )
    : textContent.trim();

  const hasImage = !!imageAttachment?.imageSrc;
  const hasText = !!finalTextContent;
  const hasContactCard = !!brandKit && !!getContactCardAttachment(template);

  if (!hasImage && !hasText && !hasContactCard) {
    return (
      <Flex align="center" justify="center" p="lg" w="full">
        <Text fontSize="xs" textAlign="center" textColor="secondary">
          {t("Empty template")}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="sm">
      {hasImage && (
        <Flex
          bgColor="primary-alt"
          borderColor="primary"
          borderRadius="md"
          borderStyle="solid"
          borderWidth="1px"
          justify="center"
          p="sm"
        >
          <img
            alt={t("SMS attachment")}
            src={imageAttachment.imageSrc ?? undefined}
            style={{ maxWidth: "100%", maxHeight: "200px" }}
          />
        </Flex>
      )}
      {hasText && (
        <Text
          bgColor="primary-alt"
          borderColor="primary"
          borderRadius="md"
          borderStyle="solid"
          borderWidth="1px"
          fontSize="xs"
          p="sm"
        >
          {finalTextContent}
        </Text>
      )}
      {hasContactCard && brandKit && (
        <ContactCard bgColor="primary" brandKit={brandKit} />
      )}
    </Flex>
  );
}
